import { useEffect, useState, type ReactNode } from "react";
import type { BroadcastLayout, BroadcastWidget } from "../../lib/broadcastApi";
import {
  clampAnimMs,
  EnterExit,
  smoothEditStyle,
  type AnimSpec,
} from "./animation/enterExit";
import { overrideProps, chartPanelOverrideCss } from "./panel/chartOverride";
import { Panel } from "./panel/Panel";
import Callouts from "./widgets/Callouts";
import { ImageWidget } from "./widgets/ImageWidget";
import { NativeStatsPanel } from "./widgets/NativeStatsPanel";
import {
  AreaControl,
  HeatMap,
  ShotMap,
  TopPlayers,
} from "./widgets/ChartWidgets";
import LivePitch from "./widgets/LivePitch";
import Scoreboard from "./widgets/Scoreboard";
import type { BroadcastFeed } from "./useBroadcastFeed";

interface Props {
  layout: BroadcastLayout;
  feed: BroadcastFeed;
  /** Program output (not an editor preview): widgets glide on edits. */
  onAir?: boolean;
}

const BASE_FONT_PX = 16;

/**
 * Chroma mode must be key-clean: no drop shadows anywhere inside a widget
 * and fully opaque panels, so a keyer leaves no dark halo or tinted fill.
 * Shadows and the translucent panels stay in every other mode.
 */
export const STAGE_CSS = `@keyframes bcast-pop{from{opacity:0;transform:scale(.92)}to{opacity:1;transform:scale(1)}}
[data-background="chroma"] [data-widget-id],[data-background="chroma"] [data-widget-id] *{box-shadow:none !important;text-shadow:none !important;filter:none !important}
[data-testid="broadcast-widget-scoreboard"] [data-testid="team-logo-fallback"],[data-testid="broadcast-widget-callouts"] [data-testid="team-logo-fallback"]{font-size:20px !important;letter-spacing:-0.02em}
[data-testid="broadcast-scaled-box"] .text-xs{font-size:13.4px !important;line-height:1.4 !important}
[data-background="chroma"] [data-testid="broadcast-scoreboard"],[data-background="chroma"] [data-testid^="broadcast-callout-"]{background-color:rgb(8,15,30) !important}`;

/**
 * Minimum on-air text is 20px at text_scale 1.0. The analytics components
 * size text in rem (12 to 16px), so their content is enlarged by this base
 * factor before the producer's text scale applies.
 */
const ANALYTICS_BASE_SCALE = 1.5;

/**
 * Analytics-based widgets size their text in rem, so the producer's text
 * scale is applied as a CSS transform: the content is laid out in a box
 * 1/scale as large and scaled back to fill the widget rectangle.
 */
/** Mounted analytics charts: a Panel needs their own card chrome overridden. */
const MOUNTED_CHART_TYPES = new Set<BroadcastWidget["type"]>([
  "heat_map",
  "area_control",
  "shot_map",
  "top_players",
]);

const SCALED_TYPES = new Set<BroadcastWidget["type"]>([
  "stats_panel",
  "top_players",
  "heat_map",
  "area_control",
  "shot_map",
]);

function ScaledBox({
  scale,
  children,
}: {
  scale: number;
  children: ReactNode;
}) {
  const s = scale > 0 ? scale : 1;
  return (
    <div
      data-testid="broadcast-scaled-box"
      data-content-scale={String(s)}
      style={{
        width: `${100 / s}%`,
        height: `${100 / s}%`,
        transform: `scale(${s})`,
        transformOrigin: "0 0",
      }}
    >
      {children}
    </div>
  );
}

function renderWidget(w: BroadcastWidget, feed: BroadcastFeed) {
  const { match, events, callout, getPlaybackNowMs } = feed;
  switch (w.type) {
    case "scoreboard":
      return match ? (
        <Scoreboard
          match={match}
          events={events}
          getPlaybackNowMs={getPlaybackNowMs}
          options={w.options}
        />
      ) : null;
    case "live_pitch":
      return match ? (
        <LivePitch match={match} events={events} options={w.options} />
      ) : null;
    case "callouts":
      return <Callouts callout={callout.current} match={match} />;
    case "stats_panel":
      return match ? (
        <NativeStatsPanel match={match} events={events} options={w.options} />
      ) : null;
    case "image":
      return <ImageWidget options={w.options} assets={feed.assets} />;
    case "top_players":
      return match ? (
        <TopPlayers
          match={match}
          events={events}
          options={w.options}
          getPlaybackNowMs={getPlaybackNowMs}
        />
      ) : null;
    case "heat_map":
      return match ? (
        <HeatMap match={match} events={events} options={w.options} />
      ) : null;
    case "area_control":
      return match ? (
        <AreaControl match={match} events={events} options={w.options} />
      ) : null;
    case "shot_map":
      return match ? (
        <ShotMap match={match} events={events} options={w.options} />
      ) : null;
    default:
      return null;
  }
}

const animActive = (a: AnimSpec | null | undefined): a is AnimSpec =>
  !!a && a.type !== "none" && clampAnimMs(a.duration_ms) > 0;

/**
 * One positioned widget. A hidden widget with an exit animation stays mounted
 * for the exit duration; without one it is gone at once (legacy behaviour).
 */
function WidgetSlot({
  w,
  feed,
  global,
  onAir,
}: {
  w: BroadcastWidget;
  feed: BroadcastFeed;
  global: number;
  onAir: boolean;
}) {
  const exitMs = animActive(w.exit) ? clampAnimMs(w.exit.duration_ms) : 0;
  const [present, setPresent] = useState(w.visible);
  useEffect(() => {
    if (w.visible) {
      setPresent(true);
      return;
    }
    if (exitMs === 0) {
      setPresent(false);
      return;
    }
    const id = setTimeout(() => setPresent(false), exitMs);
    return () => clearTimeout(id);
  }, [w.visible, exitMs]);
  if (!w.visible && (exitMs === 0 || !present)) return null;

  const panelSet = !!w.panel;
  const body = SCALED_TYPES.has(w.type) ? (
    <div
      style={{ width: "100%", height: "100%" }}
      {...overrideProps(panelSet && MOUNTED_CHART_TYPES.has(w.type))}
    >
      <ScaledBox scale={ANALYTICS_BASE_SCALE * global * w.text_scale}>
        {renderWidget(w, feed)}
      </ScaledBox>
    </div>
  ) : (
    renderWidget(w, feed)
  );
  const panelled = (
    <Panel panel={w.panel} assets={feed.assets}>
      {body}
    </Panel>
  );
  const animated =
    animActive(w.enter) || animActive(w.exit) ? (
      <EnterExit
        visible={w.visible}
        enter={w.enter ?? undefined}
        exit={w.exit ?? undefined}
      >
        {panelled}
      </EnterExit>
    ) : (
      panelled
    );
  return (
    <div
      data-testid={`broadcast-widget-${w.type}`}
      data-widget-id={w.id}
      style={{
        position: "absolute",
        left: w.x,
        top: w.y,
        width: w.w,
        height: w.h,
        zIndex: w.z,
        opacity: w.opacity,
        fontSize: BASE_FONT_PX * global * w.text_scale,
        ...smoothEditStyle(onAir),
      }}
    >
      {animated}
    </div>
  );
}

/** The widgets of one layout, absolutely positioned in stage pixels. */
export default function StageWidgets({ layout, feed, onAir = false }: Props) {
  const ordered = [...layout.widgets].sort((a, b) => a.z - b.z);
  const global = layout.stage.global_text_scale;
  return (
    <>
      <style>{`${STAGE_CSS}\n${chartPanelOverrideCss()}`}</style>
      {ordered.map((w) => (
        <WidgetSlot
          key={w.id}
          w={w}
          feed={feed}
          global={global}
          onAir={onAir}
        />
      ))}
    </>
  );
}
